import L from 'leaflet';
import { cellToBoundary, cellToLatLng, latLngToCell, cellToParent } from 'h3-js';

const FALLBACK_RESOLUTIONS = [2, 3, 4, 5, 6];
const COLOR_STOPS = [
  { t: 0, color: [254, 217, 118] },
  { t: 0.35, color: [254, 178, 76] },
  { t: 0.6, color: [253, 141, 60] },
  { t: 0.8, color: [252, 78, 42] },
  { t: 1, color: [227, 26, 28] }
];

const TOP_TOPIC_LIMIT = 8;
const LNG_WRAP = 360;
const LNG_HALF_WRAP = 180;

function wrapLngNear(lng, referenceLng) {
  if (!Number.isFinite(lng) || !Number.isFinite(referenceLng)) return lng;
  let adjusted = lng;
  while (adjusted - referenceLng > LNG_HALF_WRAP) adjusted -= LNG_WRAP;
  while (adjusted - referenceLng < -LNG_HALF_WRAP) adjusted += LNG_WRAP;
  return adjusted;
}

function normalizeBoundary(boundary, referenceLng) {
  if (!Array.isArray(boundary) || boundary.length === 0) return boundary;
  if (!Number.isFinite(referenceLng)) return boundary;
  const normalized = [];
  let prevLng = null;
  for (const point of boundary) {
    if (!Array.isArray(point) || point.length < 2) continue;
    const lat = point[0];
    const lng = point[1];
    const ref = Number.isFinite(prevLng) ? prevLng : referenceLng;
    const wrappedLng = wrapLngNear(lng, ref);
    normalized.push([lat, wrappedLng]);
    prevLng = wrappedLng;
  }
  return normalized.length ? normalized : boundary;
}

function interpolateColor(a, b, t) {
  return [
    Math.round(a[0] + (b[0] - a[0]) * t),
    Math.round(a[1] + (b[1] - a[1]) * t),
    Math.round(a[2] + (b[2] - a[2]) * t)
  ];
}

function coarsenHexPayload(payload, targetResolution) {
  if (!payload || !Array.isArray(payload.cells) || payload.cells.length === 0) return null;
  const sourceResolution = Number(payload.resolution);
  if (!Number.isFinite(targetResolution) || !Number.isFinite(sourceResolution)) return null;
  if (sourceResolution <= targetResolution) return null;

  const extractContributors = (cell) => {
    if (Array.isArray(cell?.contributors) && cell.contributors.length) {
      return cell.contributors.filter(value => typeof value === 'string' && value.length > 0);
    }
    const legacy = cell?.sources?.__contributors;
    if (Array.isArray(legacy) && legacy.length) {
      return legacy.filter(value => typeof value === 'string' && value.length > 0);
    }
    return [];
  };

  const sourceKeyForContributor = (value) => {
    if (typeof value !== 'string') return 'fallback';
    if (value.startsWith('comment:')) return 'comment';
    if (value.startsWith('topic:')) return 'topic';
    if (value.startsWith('drawing:')) return 'drawing';
    return 'fallback';
  };

  const buckets = new Map();
  const ensureBucket = (index) => {
    let bucket = buckets.get(index);
    if (!bucket) {
      bucket = {
        index,
        weight: 0,
        count: 0,
        lastSeenAt: 0,
        topicWeights: new Map(),
        sources: {},
        contributorSet: new Set(),
        fallbackWeight: 0,
        fallbackCount: 0
      };
      buckets.set(index, bucket);
    }
    return bucket;
  };

  const addTopicWeight = (bucket, topic) => {
    if (!topic) return;
    const topicId = typeof topic.topicId === 'string' && topic.topicId ? topic.topicId : null;
    const topicName = typeof topic.name === 'string' && topic.name ? topic.name : null;
    if (!topicId && !topicName) return;
    const weight = Number(topic.weight) || 0;
    if (!weight) return;
    const key = topicId ? `id:${topicId}` : `name:${topicName}`;
    const current = bucket.topicWeights.get(key);
    if (current) {
      current.weight += weight;
      if (!current.topicId && topicId) current.topicId = topicId;
      if (!current.name && topicName) current.name = topicName;
      return;
    }
    bucket.topicWeights.set(key, {
      name: topicName || 'Untitled',
      topicId,
      weight
    });
  };

  for (const cell of payload.cells) {
    if (!cell?.index) continue;
    let parentIndex;
    try {
      parentIndex = cellToParent(cell.index, targetResolution);
    } catch (err) {
      console.warn(`[hotspots] failed to derive parent for ${cell.index} -> res ${targetResolution}`, err);
      continue;
    }
    if (!parentIndex) continue;
    const bucket = ensureBucket(parentIndex);
    const weight = Number(cell.weight) || 0;
    const count = Number(cell.count ?? cell.weight ?? 0) || 0;
    const contributors = extractContributors(cell);
    if (contributors.length) {
      for (const contributor of contributors) {
        if (bucket.contributorSet.has(contributor)) continue;
        bucket.contributorSet.add(contributor);
        const key = sourceKeyForContributor(contributor);
        bucket.sources[key] = (bucket.sources[key] || 0) + 1;
      }
    } else {
      bucket.fallbackWeight += weight;
      bucket.fallbackCount += count;
      const sources = cell.sources && typeof cell.sources === 'object' ? cell.sources : {};
      for (const [key, value] of Object.entries(sources)) {
        if (key === '__contributors') continue;
        const numeric = Number(value) || 0;
        if (!numeric) continue;
        bucket.sources[key] = (bucket.sources[key] || 0) + numeric;
      }
    }
    bucket.weight = bucket.fallbackWeight + bucket.contributorSet.size;
    bucket.count = bucket.fallbackCount + bucket.contributorSet.size;
    if (cell.lastSeenAt) {
      const ts = new Date(cell.lastSeenAt).valueOf();
      if (Number.isFinite(ts)) {
        bucket.lastSeenAt = Math.max(bucket.lastSeenAt, ts);
      }
    }
    const topics = Array.isArray(cell.topTopics) ? cell.topTopics : [];
    topics.forEach(topic => addTopicWeight(bucket, topic));
  }

  if (!buckets.size) return null;

  const cells = [];
  let maxWeight = 0;
  let minWeight = Infinity;
  let sumWeight = 0;

  for (const bucket of buckets.values()) {
    const topTopics = Array.from(bucket.topicWeights.values())
      .sort((a, b) => b.weight - a.weight || String(a.name || '').localeCompare(String(b.name || '')))
      .slice(0, TOP_TOPIC_LIMIT);
    const weight = bucket.weight;
    sumWeight += weight;
    maxWeight = Math.max(maxWeight, weight);
    minWeight = Math.min(minWeight, weight);
    const sources = {};
    for (const [key, value] of Object.entries(bucket.sources)) {
      if (Number.isFinite(value) && value !== 0) {
        sources[key] = value;
      }
    }
    const contributors = Array.from(bucket.contributorSet || []);
    if (contributors.length) {
      sources.__contributors = contributors;
    }
    cells.push({
      index: bucket.index,
      weight,
      count: bucket.count || weight,
      lastSeenAt: bucket.lastSeenAt ? new Date(bucket.lastSeenAt).toISOString() : null,
      topTopics,
      sources,
      contributors
    });
  }

  cells.sort((a, b) => b.weight - a.weight);

  const stats = {
    cellCount: cells.length,
    maxWeight: maxWeight || 0,
    minWeight: Number.isFinite(minWeight) ? minWeight : 0,
    sumWeight
  };

  const available = new Set(
    (Array.isArray(payload.availableResolutions) ? payload.availableResolutions : [])
      .map(v => Number.parseInt(v, 10))
      .filter(Number.isFinite)
  );
  available.add(targetResolution);
  const availableResolutions = Array.from(available).sort((a, b) => a - b);

  if (typeof console !== 'undefined') {
    console.log(
      `[hotspots] coarsened hex payload from res ${sourceResolution} -> ${targetResolution} (${cells.length} cells)`
    );
  }

  return {
    ...payload,
    resolution: targetResolution,
    availableResolutions,
    cells,
    stats,
    derivedFromResolution: sourceResolution
  };
}

function colorRamp(value) {
  const t = Math.max(0, Math.min(1, value));
  for (let i = 0; i < COLOR_STOPS.length - 1; i += 1) {
    const left = COLOR_STOPS[i];
    const right = COLOR_STOPS[i + 1];
    if (t <= right.t) {
      const span = right.t - left.t || 1;
      const ratio = (t - left.t) / span;
      const [r, g, b] = interpolateColor(left.color, right.color, ratio);
      return `rgb(${r}, ${g}, ${b})`;
    }
  }
  const last = COLOR_STOPS[COLOR_STOPS.length - 1];
  return `rgb(${last.color[0]}, ${last.color[1]}, ${last.color[2]})`;
}

function chooseResolution(zoom, available = FALLBACK_RESOLUTIONS) {
  const target =
    zoom <= 3 ? 1 :
      zoom < 6 ? 2 :
        zoom < 8 ? 3 :
          zoom < 10 ? 4 :
            zoom < 12 ? 5 :
              6;
  if (!Array.isArray(available) || !available.length) {
    return target;
  }
  if (available.includes(target)) {
    return target;
  }
  const sorted = [...available].sort((a, b) => a - b);
  const smallest = sorted[0];
  const largest = sorted[sorted.length - 1];
  if (target < smallest) return target;
  if (target > largest) {
    return largest;
  }
  const lower = [...sorted].reverse().find(res => res < target);
  const higher = sorted.find(res => res > target);
  if (Number.isFinite(lower) && Number.isFinite(higher)) {
    return target - lower <= higher - target ? lower : higher;
  }
  return Number.isFinite(lower) ? lower : Number.isFinite(higher) ? higher : target;
}

export function initHotspots(map, { API_BASE, enabled: initialEnabled = true } = {}) {
  const paneName = 'hex-heatmap-pane';
  if (!map.getPane(paneName)) {
    const pane = map.createPane(paneName);
    if (pane) {
      pane.style.zIndex = '450';
      pane.style.pointerEvents = 'none';
    }
  }

  let eTag = null;
  let activeResolution = null;
  let activePayload = null;
  let activeCellMap = new Map();
  let availableResolutions = null;
  const resolutionCache = new Map();
  let layerGroup = null;
  let enabled = Boolean(initialEnabled);
  let destroyed = false;
  let zoomHandler = null;
  let clickHandler = null;
  let lastRefreshAt = 0;
  let isLoading = false;
  let loadingCallback = null;
  let hexCountCallback = null;

  const baseUrl = (() => {
    const provided = API_BASE && API_BASE.replace(/\/$/, '');
    if (provided) {
      return provided.endsWith('/api') ? provided : `${provided}/api`;
    }
    if (typeof window !== 'undefined') {
      return `${window.location.origin.replace(/\/$/, '')}/api`;
    }
    return 'http://localhost:3001/api';
  })();

  const tearDownLayer = () => {
    if (layerGroup) {
      try { map.removeLayer(layerGroup); } catch { }
      layerGroup = null;
    }
    activeCellMap = new Map();
  };

  const ensureLayerGroup = () => {
    if (!enabled || destroyed) return null;
    if (!layerGroup) {
      layerGroup = L.layerGroup().addTo(map);
    } else {
      layerGroup.clearLayers();
    }
    return layerGroup;
  };

  const attachEvents = () => {
    if (zoomHandler || clickHandler) return;
    zoomHandler = () => {
      if (!enabled || destroyed) return;
      updateZoomIndicator();
      syncLayer().catch(err => console.error('[hotspots] zoom sync failed', err));
    };
    clickHandler = (e) => {
      if (!enabled || destroyed) return;
      popupAt(e.latlng);
    };
    map.on('zoomend', zoomHandler);
    map.on('click', clickHandler);
  };

  const detachEvents = () => {
    if (zoomHandler) {
      try { map.off('zoomend', zoomHandler); } catch { }
      zoomHandler = null;
    }
    if (clickHandler) {
      try { map.off('click', clickHandler); } catch { }
      clickHandler = null;
    }
  };

  const updateZoomIndicator = () => {
    // Zoom indicator removed - no-op function kept for compatibility
  };

  const createZoomIndicator = () => {
    // Zoom indicator removed - no-op function kept for compatibility
  };

  const removeZoomIndicator = () => {
    // Zoom indicator removed - no-op function kept for compatibility
  };

  const setLoadingState = (loading) => {
    if (isLoading === loading) return;
    isLoading = loading;
    if (loadingCallback) {
      loadingCallback(loading);
    }
  };

  async function fetchHexPayload(resolution, { skipEtag = false } = {}) {
    const now = Date.now();
    const shouldForceRefresh = now - lastRefreshAt >= 60 * 1000;
    const headers = !skipEtag && eTag ? { 'If-None-Match': eTag } : {};
    const forceParam = shouldForceRefresh ? '&refresh=1' : '';
    const url = `${baseUrl}/heatpoints?format=hex&res=${resolution}${forceParam}`;
    const res = await fetch(url, { headers, cache: 'no-cache' });
    if (res.status === 304) {
      const cached = resolutionCache.get(resolution);
      if (cached) return cached;
      if (!skipEtag) {
        return fetchHexPayload(resolution, { skipEtag: true });
      }
      return null;
    }
    if (!res.ok) {
      throw new Error(`Unable to fetch heatpoints (status ${res.status})`);
    }
    eTag = res.headers.get('ETag') || eTag;
    if (shouldForceRefresh) {
      lastRefreshAt = Date.now();
    }
    return res.json();
  }

  function renderHexes(payload) {
    if (!enabled || destroyed) return;
    const group = ensureLayerGroup();
    if (!group) return;
    activeCellMap = new Map();
    const MAX_HEX_RENDER = 6000;
    const cells = Array.isArray(payload?.cells) ? payload.cells : [];
    const stats = payload?.stats || {};
    const maxWeight = Math.max(stats.maxWeight || 0, 0.0001);
    const mapBounds = typeof map?.getBounds === 'function' ? map.getBounds() : null;
    const paddedBounds = mapBounds ? mapBounds.pad(0.3) : null;
    const mapCenter = typeof map?.getCenter === 'function' ? map.getCenter() : null;
    const inView = [];
    const outOfView = [];
    const getCenter = (cell) => {
      try {
        const [lat, lng] = cellToLatLng(cell.index);
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          return { lat, lng };
        }
      } catch (err) {
        console.warn('[hotspots] failed to derive cell center', err);
      }
      return null;
    };
    for (const cell of cells) {
      if (paddedBounds) {
        const center = getCenter(cell);
        if (center) {
          try {
            if (paddedBounds.contains(L.latLng(center.lat, center.lng))) {
              inView.push({ cell, center });
              continue;
            }
          } catch { }
        }
      }
      outOfView.push({ cell, center: null });
    }
    const distanceScore = (entry) => {
      if (!mapCenter || !entry.center) return Number.POSITIVE_INFINITY;
      const dLat = mapCenter.lat - entry.center.lat;
      const dLng = mapCenter.lng - entry.center.lng;
      return dLat * dLat + dLng * dLng;
    };
    const sortedInView = mapCenter
      ? [...inView]
        .map(entry => ({ ...entry, dist: distanceScore(entry) }))
        .sort((a, b) => {
          if (a.dist !== b.dist) return a.dist - b.dist;
          const wa = Number(a.cell.weight) || 0;
          const wb = Number(b.cell.weight) || 0;
          return wb - wa;
        })
      : inView;
    const prioritizedEntries = [];
    prioritizedEntries.push(...sortedInView);
    if (prioritizedEntries.length < MAX_HEX_RENDER) {
      prioritizedEntries.push(...outOfView);
    }
    const limitedCells = prioritizedEntries.slice(0, MAX_HEX_RENDER).map(entry => entry.cell);
    limitedCells.forEach((cell) => {
      activeCellMap.set(cell.index, cell);
      let boundary;
      try {
        boundary = cellToBoundary(cell.index, false).map(([lat, lng]) => [lat, lng]);
      } catch (err) {
        console.warn('[hotspots] failed to build boundary for cell', cell.index, err);
        return;
      }
      const center = getCenter(cell);
      const normalizedBoundary = center ? normalizeBoundary(boundary, center.lng) : boundary;
      const intensity = Math.max(0, Math.min(1, cell.weight / maxWeight));
      const fillColor = colorRamp(intensity);
      const fillOpacity = 0.4;//Math.min(0.6, 0.25 + intensity * 0.4);
      const strokeWeight = 0.7;
      const strokeColor = 'rgba(34, 40, 49, 0.35)';
      const polygon = L.polygon(normalizedBoundary, {
        pane: paneName,
        stroke: true,
        color: strokeColor,
        weight: strokeWeight,
        fill: true,
        fillColor,
        fillOpacity
      });
      polygon.addTo(group);
    });
    if (typeof console !== 'undefined') {
      const derived = Number.parseInt(payload?.derivedFromResolution, 10);
      const derivedFrom = Number.isFinite(derived) ? ` (derived from ${derived})` : '';
      console.log(`[hotspots] rendered ${limitedCells.length} hexes @ resolution ${payload?.resolution}${derivedFrom}`);
    }
    // Notify about hex count (for auto-pan when no hexes visible)
    if (hexCountCallback) {
      hexCountCallback(limitedCells.length);
    }
  }

  async function syncLayer(force = false) {
    if (!enabled || destroyed) return;
    createZoomIndicator();
    updateZoomIndicator();
    const zoom = map.getZoom();
    const targetResolution = chooseResolution(zoom, availableResolutions || FALLBACK_RESOLUTIONS);
    if (!force && activeResolution === targetResolution && activePayload?.cells?.length) {
      return;
    }
    let payload = force ? null : resolutionCache.get(targetResolution);
    if (!payload) {
      setLoadingState(true);
      let fetched = null;
      try {
        fetched = await fetchHexPayload(targetResolution);
      } catch (err) {
        console.error('[hotspots] failed to fetch hex payload', err);
        setLoadingState(false);
        return;
      }
      if (!fetched) {
        setLoadingState(false);
        return;
      }
      const parsedResolved = Number.parseInt(fetched.resolution, 10);
      const resolved = Number.isFinite(parsedResolved) ? parsedResolved : targetResolution;
      resolutionCache.set(resolved, fetched);
      if (resolved !== targetResolution && Number.isFinite(targetResolution)) {
        const coarsened = coarsenHexPayload(fetched, targetResolution);
        if (coarsened) {
          resolutionCache.set(targetResolution, coarsened);
          payload = coarsened;
        } else {
          resolutionCache.set(targetResolution, fetched);
          payload = fetched;
        }
      } else {
        resolutionCache.set(targetResolution, fetched);
        payload = fetched;
      }
    }
    if (!payload) return;
    const parsedResolved = Number.parseInt(payload.resolution, 10);
    const resolved = Number.isFinite(parsedResolved) ? parsedResolved : targetResolution;
    if (Number.isFinite(targetResolution) && Number.isFinite(resolved) && resolved > targetResolution) {
      const coarsened = coarsenHexPayload(payload, targetResolution);
      if (coarsened) {
        resolutionCache.set(targetResolution, coarsened);
        payload = coarsened;
      }
    }
    if (Array.isArray(payload.availableResolutions) && payload.availableResolutions.length) {
      availableResolutions = payload.availableResolutions;
    }
    const parsedActive = Number.parseInt(payload.resolution, 10);
    activeResolution = Number.isFinite(parsedActive) ? parsedActive : targetResolution;
    activePayload = payload;
    renderHexes(payload);
    setLoadingState(false);
  }

  async function refresh(force = false) {
    if (destroyed) return;
    if (force) {
      resolutionCache.clear();
      activePayload = null;
      activeCellMap = new Map();
      eTag = null;
      lastRefreshAt = 0;
    }
    if (!enabled) {
      tearDownLayer();
      return;
    }
    await syncLayer(true);
  }

  function popupAt(latlng) {
    if (!enabled || destroyed) return;
    if (!activePayload?.cells?.length || !activeResolution) return;
    let index;
    try {
      index = latLngToCell(latlng.lat, latlng.lng, activeResolution);
    } catch {
      return;
    }
    const cell = activeCellMap.get(index);
    if (!cell) return;

    const container = document.createElement('div');
    container.style.fontSize = '13px';
    container.style.lineHeight = '1.4';
    container.style.color = '#334155';
    container.style.minWidth = '200px';

    const topTopics = Array.isArray(cell.topTopics) ? cell.topTopics.slice(0, 3) : [];

    if (topTopics.length > 0) {
      const header = document.createElement('div');
      header.textContent = 'Top Topics Here:';
      header.style.fontWeight = '600';
      header.style.marginBottom = '6px';
      header.style.color = '#0f172a';
      header.style.borderBottom = '1px solid #e2e8f0';
      header.style.paddingBottom = '4px';
      container.appendChild(header);

      const list = document.createElement('ul');
      list.style.margin = '0';
      list.style.padding = '0';
      list.style.listStyle = 'none';

      topTopics.forEach(topic => {
        const item = document.createElement('li');
        item.style.marginBottom = '4px';
        item.style.display = 'flex';
        item.style.alignItems = 'baseline';
        item.style.gap = '4px';

        const bullet = document.createElement('span');
        bullet.textContent = '•';
        bullet.style.color = '#64748b';
        bullet.style.flex = '0 0 auto';

        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = topic.name || 'Untitled';
        button.style.background = 'none';
        button.style.border = 'none';
        button.style.padding = '0';
        button.style.margin = '0';
        button.style.color = '#1d4ed8';
        button.style.cursor = 'pointer';
        button.style.textAlign = 'left';
        button.style.font = 'inherit';
        button.style.whiteSpace = 'nowrap';
        button.style.overflow = 'hidden';
        button.style.textOverflow = 'ellipsis';
        button.style.flex = '1 1 auto';

        button.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          window.dispatchEvent(new CustomEvent('map:select-topic', {
            detail: {
              topicId: topic.topicId || null,
              topicTitle: topic.name || ''
            }
          }));
          map.closePopup();
        });

        item.appendChild(bullet);
        item.appendChild(button);
        list.appendChild(item);
      });
      container.appendChild(list);
    }

    const note = document.createElement('div');
    note.style.marginTop = topTopics.length ? '8px' : '0';
    note.style.color = '#64748b';
    note.style.fontStyle = 'italic';
    note.style.fontSize = '12px';
    note.textContent = 'Zoom in to view details and interact.';
    container.appendChild(note);

    // Mark that a hex popup is being opened (prevents MapClickHandler from closing it)
    window.__hexPopupJustOpened = Date.now();
    L.popup({ maxWidth: 260, closeButton: false, autoPan: false })
      .setLatLng(latlng)
      .setContent(container)
      .openOn(map);
  }

  const setEnabled = (next, { refresh: shouldRefresh } = {}) => {
    if (destroyed) return;
    const normalized = Boolean(next);
    if (normalized === enabled) return;
    enabled = normalized;
    if (enabled) {
      attachEvents();
      createZoomIndicator();
      updateZoomIndicator();
      if (shouldRefresh !== false) {
        syncLayer(true).catch(err => console.error('[hotspots] enable sync failed', err));
      }
    } else {
      detachEvents();
      tearDownLayer();
      activeResolution = null;
      activePayload = null;
      setLoadingState(false);
      if (shouldRefresh !== false) {
        resolutionCache.clear();
        eTag = null;
        lastRefreshAt = 0;
      }
      removeZoomIndicator();
    }
  };

  const destroy = () => {
    if (destroyed) return;
    destroyed = true;
    setEnabled(false, { refresh: false });
    detachEvents();
    resolutionCache.clear();
    activePayload = null;
    activeCellMap = new Map();
    eTag = null;
    lastRefreshAt = 0;
    removeZoomIndicator();
  };

  if (enabled) {
    attachEvents();
    createZoomIndicator();
    updateZoomIndicator();
    syncLayer().catch(err => console.error('[hotspots] initial load failed', err));
  }

  return {
    refresh: () => refresh(true),
    setRadius: () => { },
    setEnabled,
    isEnabled: () => enabled,
    isLoading: () => isLoading,
    onLoadingChange: (callback) => {
      loadingCallback = callback;
      // Call immediately with current state
      if (callback) callback(isLoading);
    },
    onHexCountChange: (callback) => {
      hexCountCallback = callback;
    },
    destroy
  };
}
